import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { useGetLoanApplications } from '../../../services/loanService';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas/loanApplication';
import { toast } from 'react-toastify';

const CollateralVerification = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const ALL_APPLICATIONS_PAGE_SIZE = 10000;

    // Fetch loan applications
    const { data, isLoading, error } = useGetLoanApplications({
        page: 0,
        size: ALL_APPLICATIONS_PAGE_SIZE,
        status: 'COLLATERAL_VERIFICATION'
    });

    // Handle errors with useEffect
    useEffect(() => {
        if (error) {
            toast.error(`Failed to load applications: ${(error as Error).message}`);
        }
    }, [error]);

    // Filter applications that have collaterals
    const applicationsWithCollaterals = useMemo(() => {
        if (!data?.content) return [];
        return data.content.filter(row =>
            Array.isArray(row.collaterals) && row.collaterals.length > 0
        );
    }, [data]);

    // Apply search filter
    const filteredData = useMemo(() => {
        if (!applicationsWithCollaterals.length) return [];

        if (!searchQuery.trim()) return applicationsWithCollaterals;

        const lowercasedQuery = searchQuery.toLowerCase();
        return applicationsWithCollaterals.filter((item: LoanApplication) => {
            return (
                (item.id && item.id.toLowerCase().includes(lowercasedQuery)) ||
                (item.customerNumber && item.customerNumber.toLowerCase().includes(lowercasedQuery)) ||
                (item.loanDetails?.loanAmount && item.loanDetails.loanAmount.toString().includes(lowercasedQuery)) ||
                (item.loanType && item.loanType.toLowerCase().includes(lowercasedQuery))
            );
        });
    }, [searchQuery, applicationsWithCollaterals]);

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
    };

    // Search handler
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setPage(page);
    };

    // Handle rows per page change
    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setSize(newPerPage);
        setPage(page);
    };

    // Data table columns
    const columns = [
        {
            name: 'Application ID',
            selector: (row: LoanApplication) => row.id || '',
            sortable: true,
            cell: (row: LoanApplication) => (
                <Link to={`/collateral-verification/${row.id}`} className="fw-bold text-primary">
                    {row.id}
                </Link>
            )
        },
        {
            name: 'Customer',
            selector: (row: LoanApplication) => row.customerNumber || row.individual?.fullNames || 'N/A',
            sortable: true
        },
        {
            name: 'Loan Amount',
            selector: (row: LoanApplication) => row.loanDetails?.loanAmount || 0,
            sortable: true,
            cell: (row: LoanApplication) => formatCurrency(row.loanDetails?.loanAmount || 0)
        },
        {
            name: 'Loan Type',
            selector: (row: LoanApplication) => row.loanType || 'N/A',
            sortable: true
        },
        {
            name: 'Collateral Status',
            cell: (row: LoanApplication) => {
                const hasCollaterals = Array.isArray(row.collaterals) && row.collaterals.length > 0;
                if (!hasCollaterals) {
                    return <span className="badge bg-warning">Pending</span>;
                }

                // Since we don't know the exact structure, let's simplify this
                return <span className="badge bg-warning">Pending Verification</span>;
            }
        },
        {
            name: 'Actions',
            cell: (row: LoanApplication) => (
                <div className="btn-group" role="group">
                    <Link
                        to={`/collateral-verification/${row.id}`}
                        className="btn btn-sm btn-outline-primary"
                    >
                        <i className="bi bi-clipboard-check me-1"></i> Verify
                    </Link>
                </div>
            )
        }
    ];

    // Calculate verification stats
    const verificationStats = [
        {
            title: 'Total Applications',
            value: (data?.content?.length || 0).toString(),
            icon: 'fa-list-check',
            color: 'primary'
        },
        {
            title: 'Pending Verification',
            value: applicationsWithCollaterals.length.toString() || '0',
            icon: 'fa-clock',
            color: 'warning'
        },
        {
            title: 'Verified',
            value: '0', // This would come from the API in a real implementation
            icon: 'fa-check-circle',
            color: 'success'
        },
        {
            title: 'Rejected',
            value: '0', // This would come from the API in a real implementation
            icon: 'fa-times-circle',
            color: 'danger'
        },
    ];

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Collateral Verification</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Verification Stats */}
                    <div className="row g-3 mb-3 row-deck">
                        {verificationStats.map((stat, index) => (
                            <div key={index} className="col-lg-3 col-md-6 col-sm-6">
                                <div className="card">
                                    <div className="card-body">
                                        <i className={`fa ${stat.icon} position-absolute top-0 end-0 mt-4 me-3 text-${stat.color}`} style={{ fontSize: '24px' }}></i>
                                        <div className="mb-2 text-uppercase">{stat.title}</div>
                                        <div><span className="h4">{stat.value}</span></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Applications Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">Applications Requiring Collateral Verification</h6>
                                </div>
                                <div className="card-body">
                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-3">Loading applications...</p>
                                        </div>
                                    ) : error ? (
                                        <div className="alert alert-danger">
                                            <h5 className="alert-heading">Error Loading Data</h5>
                                            <p>{(error as Error).message || 'An unknown error occurred'}</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={filteredData}
                                                pagination
                                                onChangePage={handlePageChange}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                paginationPerPage={size}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                highlightOnHover
                                                striped
                                                responsive
                                                progressPending={isLoading}
                                                noDataComponent={
                                                    <div className="text-center py-4">
                                                        <i className="bi bi-inbox fs-1 text-muted"></i>
                                                        <p className="mt-2">No applications require collateral verification at this time.</p>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CollateralVerification;
